import { IntlShape } from 'react-intl';

import { ReservationWithRateDetailsDTO } from '@/models';
import { formatPropertyDate, propertyLocalDate } from '@/lib/dates';

export const getNightCount = (
  arrival: string,
  departure: string,
  timezone: string,
): number => {
  const arrivalDate = propertyLocalDate(arrival, timezone);
  const departureDate = propertyLocalDate(departure, timezone);

  // both dates are pinned to UTC midnight; a ms diff avoids browser-local DST
  return (departureDate.getTime() - arrivalDate.getTime()) / 86400000;
};

export const formatDateWithTimezone = (
  dateString: string,
  timezone: string,
  locale?: Locale,
  format: string = 'MMM d yyyy',
): string => formatPropertyDate(dateString, timezone, format, locale);

export const getDateInterval = (
  reservation: ReservationWithRateDetailsDTO,
  timezone: string,
  locale?: Locale,
  format: string = 'P',
): string => {
  return `${formatPropertyDate(
    reservation.startDate,
    timezone,
    format,
    locale,
  )} → ${formatPropertyDate(reservation.endDate, timezone, format, locale)}`;
};

export const getCheckInLabelFromSettings = (
  intlProvider: IntlShape,
  label: string,
) => {
  const checkInOptions: Record<string, string> = {
    CHECK_IN: intlProvider.formatMessage({
      id: 'check_in_label/Check-in',
      defaultMessage: 'Check-in',
    }),
    ONLINE_CHECK_IN: intlProvider.formatMessage({
      id: 'check_in_label/Online_Check-in',
      defaultMessage: 'Online Check-in',
    }),
    PRE_CHECK_IN: intlProvider.formatMessage({
      id: 'check_in_label/Pre_Check-in',
      defaultMessage: 'Pre Check-in',
    }),
    CHECK_IN_NOW: intlProvider.formatMessage({
      id: 'check_in_label/Check-in_now',
      defaultMessage: 'Check-in Now',
    }),
    BEGIN_CHECK_IN: intlProvider.formatMessage({
      id: 'check_in_label/Begin_Check-in',
      defaultMessage: 'Begin Check-in',
    }),
    START_CHECK_IN: intlProvider.formatMessage({
      id: 'check_in_label/Start_Check-in',
      defaultMessage: 'Start Check-in',
    }),
    EXPRESS_CHECK_IN: intlProvider.formatMessage({
      id: 'check_in_label/Express_Check-in',
      defaultMessage: 'Express Check-in',
    }),
    DIGITAL_CHECK_IN: intlProvider.formatMessage({
      id: 'check_in_label/Digital_Check-in',
      defaultMessage: 'Digital Check-in',
    }),
    MOBILE_CHECK_IN: intlProvider.formatMessage({
      id: 'check_in_label/Mobile_Check-in',
      defaultMessage: 'Mobile Check-in',
    }),
  };

  return checkInOptions[label] || checkInOptions['CHECK_IN'];
};

export const getCheckInUnavailableLabelFromSettings = (
  intlProvider: IntlShape,
  label: string,
  hoursBeforeArrival: string,
) => {
  const checkInOptions: Record<string, string> = {
    CHECK_IN: intlProvider.formatMessage(
      {
        id: 'common/checkin_too_early',
        defaultMessage: 'Check-in available up to {hours} prior to arrival.',
      },
      {
        hours: hoursBeforeArrival,
      },
    ),
    ONLINE_CHECK_IN: intlProvider.formatMessage(
      {
        id: 'common/checkin_too_early/Online_Check-in',
        defaultMessage:
          'Online Check-in available up to {hours} prior to arrival.',
      },
      {
        hours: hoursBeforeArrival,
      },
    ),
    PRE_CHECK_IN: intlProvider.formatMessage(
      {
        id: 'common/checkin_too_early/Pre_Check-in',
        defaultMessage:
          'Pre Check-in available up to {hours} prior to arrival.',
      },
      {
        hours: hoursBeforeArrival,
      },
    ),
    CHECK_IN_NOW: intlProvider.formatMessage(
      {
        id: 'common/checkin_too_early',
        defaultMessage: 'Check-in available up to {hours} prior to arrival.',
      },
      {
        hours: hoursBeforeArrival,
      },
    ),
    BEGIN_CHECK_IN: intlProvider.formatMessage(
      {
        id: 'common/checkin_too_early',
        defaultMessage: 'Check-in available up to {hours} prior to arrival.',
      },
      {
        hours: hoursBeforeArrival,
      },
    ),
    START_CHECK_IN: intlProvider.formatMessage(
      {
        id: 'common/checkin_too_early',
        defaultMessage: 'Check-in available up to {hours} prior to arrival.',
      },
      {
        hours: hoursBeforeArrival,
      },
    ),
    EXPRESS_CHECK_IN: intlProvider.formatMessage(
      {
        id: 'common/checkin_too_early/Express_Check-in',
        defaultMessage:
          'Express Check-in available up to {hours} prior to arrival.',
      },
      {
        hours: hoursBeforeArrival,
      },
    ),
    DIGITAL_CHECK_IN: intlProvider.formatMessage(
      {
        id: 'common/checkin_too_early/Digital_Check-in',
        defaultMessage:
          'Digital Check-in available up to {hours} prior to arrival.',
      },
      {
        hours: hoursBeforeArrival,
      },
    ),
    MOBILE_CHECK_IN: intlProvider.formatMessage(
      {
        id: 'common/checkin_too_early/Mobile_Check-in',
        defaultMessage:
          'Mobile Check-in available up to {hours} prior to arrival.',
      },
      {
        hours: hoursBeforeArrival,
      },
    ),
  };

  return checkInOptions[label] || checkInOptions['CHECK_IN'];
};
